import React from 'react';
import {
  Badge,
  Box,
  Code,
  Divider,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  List,
  ListItem,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorMode,
  VStack,
} from '@chakra-ui/react';
import { Helmet } from 'react-helmet';
import { MdApi, MdCall, MdContentCopy, MdHttp, MdInfo, MdWebhook } from 'react-icons/md';
import Navigation from '../components/Navigation';

const endpoints = [
  { method: 'GET', path: '/api/assistants', description: 'List assistants available to the API key owner.' },
  { method: 'GET', path: '/api/phoneNumbers', description: 'List phone numbers configured for the API key owner.' },
  { method: 'POST', path: '/api/call', description: 'Start a regular outbound phone call.' },
  { method: 'POST', path: '/api/whatsapp-call', description: 'Start an outbound WhatsApp voice call.' },
  { method: 'POST', path: '/api/recordings/:callSid/signed-url', description: 'Create a temporary signed recording URL.' },
  { method: 'POST', path: '/api/setWebhook/:assistantId', description: 'Replace an assistant webhook with one legacy webhook.' },
  { method: 'DELETE', path: '/api/webhook/:assistantId', description: 'Clear all assistant webhooks.' },
  { method: 'GET', path: '/api/webhooks/:assistantId', description: 'List all assistant webhooks.' },
  { method: 'POST', path: '/api/webhooks/:assistantId', description: 'Add one assistant webhook.' },
  { method: 'DELETE', path: '/api/webhooks/:assistantId/:webhookId', description: 'Remove one assistant webhook.' },
];

const callBody = `{
  "from": "+6580000000",
  "to": "+6590000000",
  "assistantId": "ASSISTANT_ID",
  "data": {
    "name": "Jackson",
    "company": "Acme"
  },
  "context": {
    "summary": "Jackson asked about upgrading from Starter to Pro.",
    "instructions": "Focus on Pro plan benefits. Mention annual billing savings if pricing comes up."
  }
}`;

const curlExample = (baseUrl) => `curl -X POST ${baseUrl}/api/call \\
  -H "Authorization: ApiKey YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${callBody}'`;

const whatsappCurlExample = (baseUrl) => `curl -X POST ${baseUrl}/api/whatsapp-call \\
  -H "Authorization: ApiKey YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${callBody}'`;

const webhookBody = `{
  "url": "https://example.com/call-completed",
  "user": "optional-basic-auth-user",
  "password": "optional-basic-auth-password"
}`;

const recordingSignedUrlBody = `{
  "expiresInSeconds": 300
}`;

const CodeBlock = ({ children }) => {
  const { colorMode } = useColorMode();
  return (
    <Box
      as="pre"
      bg={colorMode === 'light' ? 'gray.900' : 'gray.950'}
      color="gray.50"
      borderRadius="md"
      p={4}
      overflowX="auto"
      fontSize="sm"
      lineHeight="1.6"
      whiteSpace="pre"
    >
      <code>{children}</code>
    </Box>
  );
};

const Section = ({ icon, title, children }) => {
  const { colorMode } = useColorMode();
  return (
    <Box
      bg={colorMode === 'light' ? 'white' : 'gray.800'}
      borderWidth="1px"
      borderColor={colorMode === 'light' ? 'gray.200' : 'gray.700'}
      borderRadius="md"
      p={{ base: 4, md: 6 }}
    >
      <HStack spacing={3} mb={4}>
        <Icon as={icon} boxSize={5} color="brand.500" />
        <Heading size="md">{title}</Heading>
      </HStack>
      {children}
    </Box>
  );
};

const MethodBadge = ({ method }) => {
  const scheme = method === 'GET' ? 'green' : method === 'POST' ? 'blue' : 'red';
  return <Badge colorScheme={scheme}>{method}</Badge>;
};

const ApiDocsPage = () => {
  const { colorMode } = useColorMode();
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://YOUR_APP_DOMAIN';

  return (
    <>
      <Helmet>
        <title>API Docs | AI Call System</title>
        <meta name="description" content="Developer API documentation for calls, WhatsApp calls, assistants, phone numbers, and webhooks." />
      </Helmet>
      <Flex h="100vh" bg={colorMode === 'light' ? 'gray.50' : 'gray.900'}>
        <Navigation />
        <Box p={{ base: 4, md: 8 }} flex="1" overflow="auto" width="100%">
          <VStack align="stretch" spacing={6} maxW="1180px">
            <Box>
              <HStack spacing={3} mb={2}>
                <Icon as={MdApi} boxSize={7} color="brand.500" />
                <Heading size="lg">API Documentation</Heading>
              </HStack>
              <Text color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>
                Use one standard API base URL for assistant lookup, outbound calls, WhatsApp calls, and webhook configuration.
              </Text>
            </Box>

            <Grid templateColumns={{ base: '1fr', xl: '1fr 1fr' }} gap={6}>
              <Section icon={MdInfo} title="Authentication">
                <Text mb={3}>All endpoints on this page require an API key generated from the API Keys page.</Text>
                <CodeBlock>{`Authorization: ApiKey YOUR_API_KEY`}</CodeBlock>
                <Text mt={3} fontSize="sm" color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>
                  Base URL for this deployment: <Code>{baseUrl}</Code>
                </Text>
              </Section>

            <Section icon={MdCall} title="Personalization">
                <Text mb={3}>
                  Use <Code>data</Code> for prompt variables and <Code>context</Code> for one-off call instructions.
                </Text>
                <List spacing={2} fontSize="sm">
                  <ListItem><Code>GET /api/assistants</Code> returns <Code>requiredVariables</Code> for each assistant.</ListItem>
                  <ListItem><Code>{'<<name>>'}</Code> and other variables are filled from <Code>data</Code>.</ListItem>
                  <ListItem><Code>context.summary</Code> appends call-specific background.</ListItem>
                  <ListItem><Code>context.instructions</Code> appends call-specific behavior guidance.</ListItem>
                  <ListItem>Each context field supports up to <Code>4000</Code> characters.</ListItem>
                </List>
              </Section>
            </Grid>

            <Section icon={MdHttp} title="Endpoints">
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>Method</Th>
                      <Th>Path</Th>
                      <Th>Description</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {endpoints.map((endpoint) => (
                      <Tr key={`${endpoint.method}-${endpoint.path}`}>
                        <Td><MethodBadge method={endpoint.method} /></Td>
                        <Td><Code>{endpoint.path}</Code></Td>
                        <Td>{endpoint.description}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Section>

            <Section icon={MdApi} title="Assistant Variables">
              <Text mb={3}>Fetch assistants to discover which Mustache variables should be supplied in the call payload.</Text>
              <CodeBlock>{`curl -X GET ${baseUrl}/api/assistants \\
  -H "Authorization: ApiKey YOUR_API_KEY"`}</CodeBlock>
              <Text mt={3} mb={3}>Example response item:</Text>
              <CodeBlock>{`{
  "_id": "ASSISTANT_ID",
  "name": "Sales Assistant",
  "model": "realtime-audio:gpt-4o-realtime-preview",
  "requiredVariables": ["company", "name", "plan"]
}`}</CodeBlock>
            </Section>

            <Section icon={MdCall} title="Outbound Call">
              <Text mb={3}>Start a regular outbound call.</Text>
              <CodeBlock>{curlExample(baseUrl)}</CodeBlock>
            </Section>

            <Section icon={MdCall} title="WhatsApp Call">
              <Text mb={3}>
                Start an outbound WhatsApp voice call. Use normal E.164 numbers in the request; the voice backend formats them for Twilio.
              </Text>
              <CodeBlock>{whatsappCurlExample(baseUrl)}</CodeBlock>
            </Section>

            <Section icon={MdWebhook} title="Webhooks">
              <Text mb={3}>Add a webhook for an assistant. Use the returned webhook ID to delete a specific webhook later.</Text>
              <CodeBlock>{`curl -X POST ${baseUrl}/api/webhooks/ASSISTANT_ID \\
  -H "Authorization: ApiKey YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${webhookBody}'`}</CodeBlock>
              <Divider my={4} />
              <Text mb={3}>List and remove webhooks.</Text>
              <CodeBlock>{`curl -X GET ${baseUrl}/api/webhooks/ASSISTANT_ID \\
  -H "Authorization: ApiKey YOUR_API_KEY"

curl -X DELETE ${baseUrl}/api/webhooks/ASSISTANT_ID/WEBHOOK_ID \\
  -H "Authorization: ApiKey YOUR_API_KEY"`}</CodeBlock>
            </Section>

            <Section icon={MdContentCopy} title="Recording Access">
              <Text mb={3}>
                Generate a temporary URL for an external app to retrieve a call recording without exposing Twilio credentials.
              </Text>
              <CodeBlock>{`curl -X POST ${baseUrl}/api/recordings/CALL_SID/signed-url \\
  -H "Authorization: ApiKey YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${recordingSignedUrlBody}'`}</CodeBlock>
              <Text mt={3} mb={3}>Response:</Text>
              <CodeBlock>{`{
  "success": true,
  "url": "${baseUrl}/api/recordings/TEMPORARY_TOKEN",
  "expiresInSeconds": 300,
  "expiresAt": "2026-04-25T10:30:00.000Z"
}`}</CodeBlock>
              <List spacing={2} mt={3}>
                <ListItem>The generated URL does not require the API key.</ListItem>
                <ListItem>Expiry is clamped between <Code>60</Code> and <Code>3600</Code> seconds.</ListItem>
                <ListItem>The server verifies ownership again before streaming the recording.</ListItem>
              </List>
            </Section>

            <Section icon={MdContentCopy} title="Response Notes">
              <List spacing={2}>
                <ListItem>Successful call endpoints return the backend call response, including <Code>callSid</Code>.</ListItem>
                <ListItem><Code>GET /api/assistants</Code> omits full assistant prompts from the response.</ListItem>
                <ListItem><Code>POST /api/call</Code> and <Code>POST /api/whatsapp-call</Code> are proxied to the configured voice backend.</ListItem>
                <ListItem>Signed recording URLs stream through this app server, which can authenticate to Twilio for private recordings.</ListItem>
                <ListItem>Existing backend-domain API endpoints remain available for backward compatibility.</ListItem>
              </List>
            </Section>
          </VStack>
        </Box>
      </Flex>
    </>
  );
};

export default ApiDocsPage;
